import { getApiUrl } from './config';
import { clearAdminSession } from './admin-session';

/** Misafir/üye ipucu — HttpOnly cookie JS’ten okunamadığı için localStorage */
export const AUTH_CLIENT_STATE_KEY = 'haber_auth_state'

export type AuthClientState = 'user' | 'guest'

export function getAuthClientState(): AuthClientState | null {
  if (typeof window === 'undefined') return null
  try {
    const v = localStorage.getItem(AUTH_CLIENT_STATE_KEY)
    if (v === 'user' || v === 'guest') return v
  } catch {
    /* ignore */
  }
  return null
}

export function setAuthClientState(state: AuthClientState): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(AUTH_CLIENT_STATE_KEY, state)
  } catch {
    /* ignore */
  }
}

/** Yalnızca bilinen oturumda refresh dene — anonim ilk ziyarette 401 üretme. */
export function shouldAttemptTokenRefresh(): boolean {
  return getAuthClientState() === 'user'
}

const API_ERROR_TR: Record<string, string> = {
  'Code is required': 'Promo kod zorunludur.',
  'Invalid or inactive code': 'Geçersiz veya pasif promo kod.',
  'Geçersiz veya pasif promo kod': 'Geçersiz veya pasif promo kod.',
  'Code has reached maximum redemptions': 'Bu kod kullanım limitine ulaştı.',
  'Bu kod kullanım limitine ulaştı': 'Bu kod kullanım limitine ulaştı.',
  'You have already redeemed this code': 'Bu kodu daha önce kullandınız.',
  'Bu kodu daha önce kullandınız': 'Bu kodu daha önce kullandınız.',
  'Promo code has no duration configured': 'Promo kodunun süresi tanımlı değil.',
  'Promo kodunun süresi tanımlı değil': 'Promo kodunun süresi tanımlı değil.',
  'Promo kodunun süresi dolmuş': 'Promo kodunun süresi dolmuş',
  'Unauthorized': 'Oturumunuz sona ermiş. Lütfen tekrar giriş yapın.',
  'No token provided': 'Oturum bulunamadı. Lütfen tekrar giriş yapın.',
  'Invalid token': 'Oturum geçersiz. Lütfen tekrar giriş yapın.',
};

function localizeApiMessage(message: string): string {
  const trimmed = message.trim();
  if (API_ERROR_TR[trimmed]) return API_ERROR_TR[trimmed];
  if (trimmed.includes('must be')) return 'Geçersiz istek. Lütfen tekrar deneyin.';
  return trimmed;
}

async function parseApiError(res: Response): Promise<string> {
  const data = (await res.json().catch(() => null)) as {
    message?: string | string[];
  } | null;

  if (!data?.message) {
    if (res.status === 400) return 'Geçersiz promo kod.';
    if (res.status === 401) return 'Oturumunuz sona ermiş. Lütfen tekrar giriş yapın.';
    return `İstek başarısız (${res.status})`;
  }

  if (Array.isArray(data.message)) {
    return localizeApiMessage(data.message.join(' '));
  }

  return localizeApiMessage(data.message);
}

export interface User {
  id: number;
  username: string;
  email: string | null;
  phone: string | null;
  name: string | null;
  surname?: string | null;
  role: string;
  createdAt: string;
  customRole?: {
    id: number;
    name: string;
    description: string | null;
    isActive: boolean;
  } | null;
  customRoleId?: number | null;
  avatar?: string | null;
  provider?: string | null;
  signupChannel?: string | null;
  /** Haber formunda yapay zeka araçları */
  aiEnabled?: boolean;
  roles?: Array<{ id: number; name: string; slug: string }>;
  policy?: Policy;
}

export interface Membership {
  plan: 'STANDARD' | 'PREMIUM';
  status: 'ACTIVE' | 'EXPIRED';
  premiumUntil: string | null;
  isPremium: boolean;
}

export interface Policy {
  resources?: Record<string, {
    actions: Record<string, 'ALL' | 'OWN' | 'NONE'>;
  }>;
  ui?: {
    menu?: Record<string, boolean>;
    elements?: Record<string, boolean>;
  };
  workflow?: Record<string, any>;
}

export interface MeResponse {
  user: User | null;
  membership: Membership | null;
  policy?: Policy | null;
}

export const DEFAULT_MEMBERSHIP: Membership = {
  plan: 'STANDARD',
  status: 'ACTIVE',
  premiumUntil: null,
  isPremium: false,
};

export function normalizeMembership(raw: unknown): Membership | null {
  if (!raw || typeof raw !== 'object') return null;

  const value = raw as Record<string, unknown>;
  const plan = value.plan === 'PREMIUM' ? 'PREMIUM' : 'STANDARD';
  const status = value.status === 'EXPIRED' ? 'EXPIRED' : 'ACTIVE';
  const premiumUntil =
    typeof value.premiumUntil === 'string'
      ? value.premiumUntil
      : value.premiumUntil
        ? String(value.premiumUntil)
        : null;

  let isPremium = Boolean(value.isPremium);
  if (!isPremium && plan === 'PREMIUM' && status !== 'EXPIRED') {
    if (!premiumUntil) {
      isPremium = true;
    } else {
      const expiry = new Date(premiumUntil);
      isPremium = Number.isFinite(expiry.getTime()) && expiry > new Date();
    }
  }

  return { plan, status, premiumUntil, isPremium };
}

export function membershipFromUserRecord(user: {
  membership_tier?: string | null;
  membership_expires_at?: string | Date | null;
} | null | undefined): Membership | null {
  if (!user || user.membership_tier !== 'premium') return null;

  const premiumUntil =
    user.membership_expires_at == null
      ? null
      : typeof user.membership_expires_at === 'string'
        ? user.membership_expires_at
        : user.membership_expires_at.toISOString();

  return normalizeMembership({
    plan: 'PREMIUM',
    status: 'ACTIVE',
    premiumUntil,
  });
}

export function membershipFromRedeem(premiumUntil?: string | null): Membership {
  return {
    plan: 'PREMIUM',
    status: 'ACTIVE',
    premiumUntil: premiumUntil ?? null,
    isPremium: true,
  };
}

function resolveMembership(
  membershipFromMe: Membership | null,
  membershipFromUser: Membership | null,
): Membership {
  if (membershipFromMe?.isPremium) return membershipFromMe;
  if (membershipFromUser?.isPremium) return membershipFromUser;
  return membershipFromMe ?? membershipFromUser ?? DEFAULT_MEMBERSHIP;
}

function authHeaders(extra?: HeadersInit): HeadersInit {
  // Web auth uses HttpOnly cookies only — never mirror tokens into localStorage
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Client': 'web',
  };
  return { ...headers, ...extra };
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  try {
    const res = await fetch(`${getApiUrl()}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: authHeaders(options.headers),
    });

    if (!res.ok) {
      const errorMessage = await parseApiError(res);
      const errorObj = new Error(errorMessage);
      (errorObj as any).status = res.status;
      throw errorObj;
    }

    return res.json();
  } catch (error: any) {
    if (error.status) {
      throw error;
    }
    throw new Error(error.message || 'Bağlantı hatası oluştu');
  }
}

export async function register(data: {
  username: string;
  email?: string;
  phone?: string;
  password: string;
  name?: string;
}) {
  return fetchApi('/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function login(identifier: string, password: string) {
  try {
    const response = await fetchApi<{
      message: string;
      accessToken?: string;
      refreshToken?: string;
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ identifier, password }),
    });
    // Tokens stay in HttpOnly cookies set by the API — do not persist in JS storage
    if (typeof window !== 'undefined') {
      localStorage.removeItem('token');
      setAuthClientState('user');
    }
    return response;
  } catch (error: any) {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('token');
    }
    throw error;
  }
}

export async function logout() {
  try {
    await fetchApi('/auth/logout', {
      method: 'POST',
    });
  } finally {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('token');
      clearAdminSession();
      setAuthClientState('guest');
    }
  }
}

export async function refreshToken() {
  if (!shouldAttemptTokenRefresh()) {
    const err = new Error('No refresh token');
    (err as { status?: number }).status = 401;
    throw err;
  }

  try {
    const response = await fetchApi<{
      message: string;
      accessToken?: string;
      refreshToken?: string;
    }>('/auth/refresh', {
      method: 'POST',
    });

    if (typeof window !== 'undefined') {
      localStorage.removeItem('token');
      setAuthClientState('user');
    }

    return response;
  } catch (error) {
    if (typeof window !== 'undefined') {
      setAuthClientState('guest');
    }
    throw error;
  }
}

export async function getMe(): Promise<MeResponse> {
  try {
    const response = await fetch(`${getApiUrl()}/me`, {
      credentials: 'include',
      headers: authHeaders(),
      cache: 'no-store',
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized');
      }
      if (response.status === 500) {
        const errorData = await response.json().catch(() => ({ message: 'Server error' }));
        if (errorData.user === null) {
          throw new Error('Unauthorized');
        }
        throw new Error('Server error');
      }
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.message || `Failed to fetch user: ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.user) {
      throw new Error('Unauthorized');
    }

    const membershipFromMe = normalizeMembership(data.membership);
    const membershipFromUser = membershipFromUserRecord(data.user);
    const membership = resolveMembership(membershipFromMe, membershipFromUser);

    return {
      user: data.user,
      policy: data.policy || null,
      membership,
    };
  } catch (error: any) {
    if (error.message === 'Unauthorized') {
      throw error;
    }
    throw new Error(error.message || 'Failed to fetch user data');
  }
}

export async function updateProfile(data: {
  name: string;
  surname?: string;
  username?: string;
}) {
  return fetchApi<{ message: string; user?: User }>('/auth/update-profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function uploadAvatar(file: File): Promise<{
  message: string;
  path: string;
  url?: string;
  user?: User;
}> {
  const form = new FormData();
  form.append('file', file);

  const headers: Record<string, string> = {
    'X-Client': 'web',
  };

  const res = await fetch(`${getApiUrl()}/auth/avatar`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: form,
  });

  if (!res.ok) {
    throw new Error(await parseApiError(res));
  }

  return res.json();
}

export async function removeAvatar(): Promise<{
  message: string;
  user?: User;
}> {
  return fetchApi('/auth/avatar', {
    method: 'DELETE',
  });
}

export async function forgotPassword(identifier: string) {
  return fetchApi('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ identifier }),
  });
}

export async function resetPassword(token: string, newPassword: string) {
  return fetchApi('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, newPassword }),
  });
}

export async function redeemCode(code: string) {
  const result = await fetchApi<{ message: string; premiumUntil?: string | null }>(
    '/membership/redeem',
    {
      method: 'POST',
      body: JSON.stringify({ code: code.trim().toUpperCase() }),
    },
  );
  return result;
}

export function getOAuthUrl(
  provider: 'google' | 'facebook' | 'twitter' | 'apple' | 'microsoft',
): string {
  return `${getApiUrl()}/auth/${provider}`;
}



export interface OAuthStatus {
  google: { enabled: boolean };
  facebook: { enabled: boolean };
  twitter: { enabled: boolean };
  apple?: { enabled: boolean };
  microsoft?: { enabled: boolean };
}

export async function getOAuthStatus(): Promise<OAuthStatus> {
  return fetchApi<OAuthStatus>("/auth/oauth/status");
}
